import { memo } from 'react';
import { Group, Circle, Ellipse, Line, Rect, Shape } from 'react-konva';

/**
 * Agáta character - caterpillar girl with blonde pigtails,
 * green top, segmented green/yellow/orange body, antennae, big eyes.
 */
export const AgataCharacter = memo(function AgataCharacter({ x, y, scaleX, scaleY, rotation, opacity, fill, customColors, onClick, onDragEnd, onTransformEnd, draggable, isSelected }) {
  const c = customColors || {};
  const bodyColor = c.fill || fill || '#4ade80';
  const segmentYellow = c.segmentYellow || '#fbbf24';
  const segmentOrange = c.segmentOrange || '#fb923c';
  const hairColor = c.hairColor || '#fde047';
  const skinColor = c.skinColor || '#fde8d0';
  const eyeColor = '#1e1e1e';
  const greenTop = c.greenTop || '#22c55e';
  const antennaColor = '#1e1e1e';
  const hairTieColor = c.hairTieColor || '#ef4444';

  return (
    <Group
      x={x} y={y}
      scaleX={scaleX || 1} scaleY={scaleY || 1}
      rotation={rotation || 0}
      opacity={opacity ?? 1}
      draggable={draggable}
      onClick={() => onClick?.()}
      onTap={() => onClick?.()}
      onDragEnd={(e) => onDragEnd?.({ x: e.target.x(), y: e.target.y() })}
      onTransformEnd={(e) => {
        const node = e.target;
        onTransformEnd?.({
          x: node.x(), y: node.y(),
          scaleX: node.scaleX(), scaleY: node.scaleY(),
          rotation: node.rotation(),
        });
      }}
    >
      {/* Caterpillar body segments (back to front) */}
      {/* Segment 5 - tail */}
      <Ellipse x={0} y={75} radiusX={10} radiusY={8} fill={bodyColor} opacity={0.8} />
      <Circle x={0} y={75} radius={3} fill={segmentOrange} opacity={0.7} />

      {/* Segment 4 */}
      <Ellipse x={-4} y={62} radiusX={14} radiusY={10} fill={bodyColor} />
      <Circle x={-6} y={60} radius={3} fill={segmentYellow} opacity={0.7} />
      <Circle x={2} y={64} radius={2.5} fill={segmentOrange} opacity={0.6} />
      {/* Legs */}
      <Ellipse x={-16} y={64} radiusX={6} radiusY={3} fill={bodyColor} rotation={-15} />
      <Ellipse x={10} y={64} radiusX={6} radiusY={3} fill={bodyColor} rotation={15} />

      {/* Segment 3 */}
      <Ellipse x={2} y={48} radiusX={16} radiusY={11} fill={bodyColor} />
      <Circle x={-4} y={46} radius={3} fill={segmentYellow} opacity={0.7} />
      <Circle x={6} y={50} radius={2.5} fill={segmentOrange} opacity={0.6} />
      {/* Legs */}
      <Ellipse x={-20} y={50} radiusX={7} radiusY={3.5} fill={bodyColor} rotation={-10} />
      <Ellipse x={20} y={50} radiusX={7} radiusY={3.5} fill={bodyColor} rotation={10} />

      {/* Segment 2 */}
      <Ellipse x={0} y={34} radiusX={18} radiusY={12} fill={bodyColor} />
      <Circle x={-5} y={32} radius={3.5} fill={segmentYellow} opacity={0.7} />
      <Circle x={7} y={36} radius={3} fill={segmentOrange} opacity={0.6} />
      {/* Legs */}
      <Ellipse x={-22} y={36} radiusX={8} radiusY={3.5} fill={bodyColor} rotation={-8} />
      <Ellipse x={22} y={36} radiusX={8} radiusY={3.5} fill={bodyColor} rotation={8} />

      {/* Segment 1 (top, under green collar) */}
      <Ellipse x={0} y={20} radiusX={17} radiusY={10} fill={bodyColor} />

      {/* Green collar/top */}
      <Ellipse x={0} y={8} radiusX={18} radiusY={11} fill={greenTop} />
      <Ellipse x={0} y={2} radiusX={14} radiusY={8} fill={greenTop} />

      {/* Head */}
      <Circle x={0} y={-15} radius={22} fill={skinColor} />

      {/* Cheeks blush */}
      <Ellipse x={-14} y={-8} radiusX={6} radiusY={4} fill="#fca5a5" opacity={0.3} />
      <Ellipse x={14} y={-8} radiusX={6} radiusY={4} fill="#fca5a5" opacity={0.3} />

      {/* Eyes */}
      <Ellipse x={-8} y={-16} radiusX={5.5} radiusY={6} fill="white" />
      <Circle x={-8} y={-15} radius={3.5} fill={eyeColor} />
      <Circle x={-7} y={-16.5} radius={1.2} fill="white" />
      <Ellipse x={8} y={-16} radiusX={5.5} radiusY={6} fill="white" />
      <Circle x={8} y={-15} radius={3.5} fill={eyeColor} />
      <Circle x={9} y={-16.5} radius={1.2} fill="white" />

      {/* Eyebrows */}
      <Line points={[-13,-22, -8,-24, -3,-22]} stroke={eyeColor} strokeWidth={1.2} lineCap="round" tension={0.3} />
      <Line points={[3,-22, 8,-24, 13,-22]} stroke={eyeColor} strokeWidth={1.2} lineCap="round" tension={0.3} />

      {/* Nose */}
      <Circle x={0} y={-10} radius={1} fill="#e8c4a0" />

      {/* Mouth - smile */}
      <Line points={[-6,-5, -3,-3, 0,-4, 3,-3, 6,-5]} stroke="#d4845a" strokeWidth={1.5} lineCap="round" tension={0.4} />

      {/* Pigtails */}
      {/* Left pigtail */}
      <Ellipse x={-24} y={-24} radiusX={10} radiusY={14} fill={hairColor} rotation={-20} />
      <Ellipse x={-28} y={-18} radiusX={7} radiusY={10} fill={hairColor} rotation={-30} />
      {/* Hair tie left */}
      <Circle x={-20} y={-20} radius={3} fill={hairTieColor} />

      {/* Right pigtail */}
      <Ellipse x={24} y={-24} radiusX={10} radiusY={14} fill={hairColor} rotation={20} />
      <Ellipse x={28} y={-18} radiusX={7} radiusY={10} fill={hairColor} rotation={30} />
      {/* Hair tie right */}
      <Circle x={20} y={-20} radius={3} fill={hairTieColor} />

      {/* Hair top */}
      <Ellipse x={0} y={-32} radiusX={16} radiusY={8} fill={hairColor} />
      <Ellipse x={-6} y={-36} radiusX={6} radiusY={5} fill={hairColor} rotation={-10} />
      <Ellipse x={6} y={-36} radiusX={6} radiusY={5} fill={hairColor} rotation={10} />

      {/* Antennae */}
      <Line points={[-5,-38, -8,-52]} stroke={antennaColor} strokeWidth={2} lineCap="round" />
      <Circle x={-8} y={-54} radius={3} fill={antennaColor} />
      <Line points={[5,-38, 8,-52]} stroke={antennaColor} strokeWidth={2} lineCap="round" />
      <Circle x={8} y={-54} radius={3} fill={antennaColor} />

      {/* Selection highlight */}
      {isSelected && <Circle x={0} y={15} radius={60} stroke="#f472b6" strokeWidth={1.5} dash={[6,4]} fill="transparent" opacity={0.4} />}
    </Group>
  );
});
